<script lang="ts" setup>
import autoAnimate from '@formkit/auto-animate';
import { computed, onMounted, ref } from 'vue';
import { useFocusState } from '../../composables/useFocusState';
import { CheckoutSteps } from '../../services/models/checkout.models';
import { API_ERROR_MESSAGES, USER_FEEDBACK } from '../../utils/API/APIErrors';
import { getAddresses } from '../../utils/API/Bring/bringAPI';
import { AddressDetails } from '../../utils/API/Bring/bringAPISchema';
import { AddressModelSchema, postalCodeSchema } from '../../utils/API/Cart/cartAPISchema';
import * as CheckoutHelpers from '../../utils/checkoutHelpers';
import { debounce } from '../../utils/debounce';
import { FEEDBACK_TEXT, isFullNameTooLong, validationHelper } from '../../utils/validationHelpers';
import ButtonList from '../buttonList/buttonList.vue';
import FormInput from '../form/formInput.vue';
import Icon from '../icon/icon.vue';
import { useCheckoutStateStore } from './stores/checkoutStateStore';
import { useCustomerInformationStore } from './stores/customerInformationStore';

const DEBOUNCE_DELAY_MS = 250;

const props = defineProps<{
  isActive?: boolean;
}>();

const customerInformationStore = useCustomerInformationStore();
const checkoutStateStore = useCheckoutStateStore();
const addressSuggestions = ref<AddressDetails[]>([]);
const addressFormInputElement = ref<HTMLElement | null>();
const suggestionsContainerElement = ref<HTMLElement | null>();

const {
  focused: addressSuggestionsVisible,
  handleFocus: showAddressSuggestions,
  handleBlur: hideAddressSuggestions,
} = useFocusState([addressFormInputElement, suggestionsContainerElement], true);

const submitForm = () => {
  checkoutStateStore.goToStep(CheckoutSteps.Delivery);
};

const isNameTooLong = () =>
  isFullNameTooLong(
    customerInformationStore.fields?.firstName || '',
    customerInformationStore.fields?.lastName || ''
  );

const validateField = (path: string) => {
  const fieldValue = customerInformationStore.fields?.[path];
  const fieldSchema = AddressModelSchema.shape?.[path];

  if ((path === 'firstName' || path === 'lastName') && isNameTooLong()) {
    customerInformationStore.setError(path, [FEEDBACK_TEXT().NAME_MAXLENGTH_EXCEEDED]);
    return;
  }

  const validationResult = fieldSchema
    ? fieldSchema.safeParse(fieldValue)
    : AddressModelSchema.safeParse(customerInformationStore.fields);

  if (validationResult.success) {
    customerInformationStore.setError(path, []);
  } else {
    const pathRelatedErrors = validationResult.error.issues.filter(
      (issue) => issue.path.toString() === path
    );
    const errorMessages = pathRelatedErrors.map((issue) => issue.message);
    customerInformationStore.setError(path, errorMessages);
  }
};

const handlePostalPlaceBlur = async () => {
  const validationResult = postalCodeSchema.safeParse(customerInformationStore.fields?.postalCode);

  if (!validationResult.success) {
    const errorMessages = validationResult.error.issues.map((issue) => issue.message);
    customerInformationStore.setError('postalCode', errorMessages);
    return;
  }

  const matchingCityResult = await customerInformationStore.tryGetCityMatchingPostalCode(
    validationResult.data
  );

  if (!matchingCityResult || matchingCityResult === API_ERROR_MESSAGES.COULD_NOT_GET_CITY) {
    return;
  }

  if (matchingCityResult === USER_FEEDBACK.UNKNOWN_POSTALCODE) {
    customerInformationStore.setError('postalCode', [USER_FEEDBACK.UNKNOWN_POSTALCODE]);
    return;
  }

  clearPostalAndCityErrors();
};

const clearPostalAndCityErrors = () => {
  customerInformationStore.setError('postalCode', []);
  customerInformationStore.setError('city', []);
};

const handlePostalCodeInput = async (postalCode: string) => {
  addressSuggestions.value = [];
  customerInformationStore.setField('bringAddressId', '');
  customerInformationStore.setField('postalCode', postalCode);

  if (!validationHelper.isPostalCode(postalCode)) return;

  const matchingCityResult = await customerInformationStore.tryGetCityMatchingPostalCode(postalCode);
  if (!matchingCityResult || matchingCityResult === API_ERROR_MESSAGES.COULD_NOT_GET_CITY) return;

  if (matchingCityResult === USER_FEEDBACK.UNKNOWN_POSTALCODE) {
    customerInformationStore.setError('postalCode', [USER_FEEDBACK.UNKNOWN_POSTALCODE]);
    return;
  }

  customerInformationStore.setField('city', CheckoutHelpers.sentenceCase(matchingCityResult));
  clearPostalAndCityErrors();
};

const addressSuggestionsAsOptions = computed(() =>
  CheckoutHelpers.mapAddressesToSuggestionOptions(addressSuggestions.value)
);

const addressSuggestionsAriaLiveText = computed(() =>
  addressSuggestions.value.length
    ? `${addressSuggestions.value.length} adresseforslag for ${customerInformationStore.fields.address}`
    : 'Ingen adresseforslag'
);

const fetchAddressSuggestions = debounce(async (address: string) => {
  customerInformationStore.setField('address', address);

  const suggestions = await getAddresses(address);
  if (suggestions?.length) {
    addressSuggestions.value = suggestions;
    customerInformationStore.setMostRecentAddressSuggestions(suggestions);
    return;
  }

  addressSuggestions.value = [];
}, DEBOUNCE_DELAY_MS);

const debouncedHandleAddressInput = (address: string) => {
  if (!address) {
    addressSuggestions.value = [];
    return;
  }

  validateField('address');
  if (customerInformationStore.errors.address?.length) {
    addressSuggestions.value = [];
    return;
  }

  fetchAddressSuggestions(address);
};

const handleSelectAddressFromSuggestions = (id: string) => {
  const selectedAddress = addressSuggestions.value.find((address) => address.addressId === id);

  if (selectedAddress) {
    customerInformationStore.setField('address', CheckoutHelpers.formatAddress(selectedAddress));
    customerInformationStore.setField('postalCode', selectedAddress.postalCode);
    customerInformationStore.setField('city', selectedAddress.city);
    customerInformationStore.setField('bringAddressId', selectedAddress.addressId);
  }

  addressSuggestions.value = [];
  addressFormInputElement.value?.focus();
  validateField('address');
  validateField('postalCode');
  validateField('city');
};

const handleAddressBlur = () => {
  validateField('address');
  hideAddressSuggestions();
};

onMounted(() => {
  if (suggestionsContainerElement.value) autoAnimate(suggestionsContainerElement.value);
});
</script>

<template>
  <div>
    <div v-if="!props.isActive && customerInformationStore.fields" class="checkout-form__inactive">
      <div>
        <div>
          <span
            >{{ customerInformationStore.fields?.firstName }}
            {{ customerInformationStore.fields?.lastName }}</span
          >
        </div>
        <div>
          <span
            >{{ customerInformationStore.fields?.address }},
            {{ customerInformationStore.fields?.postalCode }}
            {{ customerInformationStore.fields?.city?.toUpperCase() }}</span
          >
        </div>
        <span
          >{{ customerInformationStore.fields?.email }},
          {{ customerInformationStore.fields?.phoneNumber }}</span
        >
      </div>
    </div>
    <form v-if="props.isActive" class="checkout-form" novalidate @submit.prevent="submitForm">
      <fieldset class="checkout-form__fieldset--customerinformation">
        <legend>{{ $t('customerInformation') }}</legend>
        <div
          v-if="
            customerInformationStore.errorSummaryVisible && customerInformationStore.errorsExist
          "
          ref="customerInformationErrorsElement"
          tabindex="-1"
          class="checkout-form__error-summary"
        >
          <h3 v-if="customerInformationStore.errorsExist">
            {{ $t('errorsInForm') }}
          </h3>
          <ul
            role="alert"
            class="checkout-form__error-list"
            :aria-hidden="customerInformationStore.errorsExist"
          >
            <template v-for="(messages, path) in customerInformationStore.errors">
              <li v-if="messages[0]" :key="path" class="checkout-form__error">
                {{ $t(path) }}: {{ messages[0] }}
              </li>
            </template>
          </ul>
        </div>
        <FormInput
          v-model="customerInformationStore.fields.email"
          input-class="checkout-form__input"
          :label="$t('email')"
          :error="customerInformationStore.errors.email?.[0]"
          label-class="checkout-form__label"
          type="email"
          :required="true"
          :autofocus="true"
          autocomplete="email"
          @blur="validateField('email')"
        />
        <div class="checkout-form__row">
          <FormInput
            v-model="customerInformationStore.fields.firstName"
            :label="$t('firstName').toString()"
            :error="customerInformationStore.errors.firstName?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="given-name"
            @blur="validateField('firstName')"
          />
          <FormInput
            v-model="customerInformationStore.fields.lastName"
            :label="$t('lastName').toString()"
            :error="customerInformationStore.errors.lastName?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="family-name"
            @blur="validateField('lastName')"
          />
        </div>
        <FormInput
          v-model="customerInformationStore.fields.phoneNumber"
          :label="$t('phoneNumber').toString()"
          :error="customerInformationStore.errors.phoneNumber?.[0]"
          input-class="checkout-form__input"
          label-class="checkout-form__label"
          type="tel"
          :required="true"
          autocomplete="tel-national"
          @blur="validateField('phoneNumber')"
        />
        <FormInput
          ref="addressFormInputElement"
          v-model="customerInformationStore.fields.address"
          :label="$t('address').toString()"
          :error="customerInformationStore.errors.address?.[0]"
          input-class="checkout-form__input"
          label-class="checkout-form__label"
          type="text"
          :required="true"
          autocomplete="street-address"
          @input="debouncedHandleAddressInput($event.target.value)"
          @blur="handleAddressBlur"
          @focusin="showAddressSuggestions"
        />
        <div
          ref="suggestionsContainerElement"
          tabindex="-1"
          @focusin="showAddressSuggestions"
          @focusout="hideAddressSuggestions"
        >
          <ButtonList
            v-if="addressSuggestionsVisible && addressSuggestionsAsOptions.length"
            v-model="customerInformationStore.fields.address"
            :heading="$t('addressSuggestions').toString()"
            :options="addressSuggestionsAsOptions"
            :aria-live-text="addressSuggestionsAriaLiveText"
            @update:modelValue="handleSelectAddressFromSuggestions"
          />
        </div>
        <div class="checkout-form__row">
          <FormInput
            v-model="customerInformationStore.fields.postalCode"
            :label="$t('postalCode').toString()"
            :error="customerInformationStore.errors.postalCode?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="postal-code"
            @input="handlePostalCodeInput($event.target.value)"
            @blur="handlePostalPlaceBlur"
          />
          <FormInput
            v-model="customerInformationStore.fields.city"
            :label="$t('city').toString()"
            :error="customerInformationStore.errors.city?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="address-level2"
            :disabled="customerInformationStore.retrievingCity"
            @blur="validateField('city')"
          />
        </div>
        <button
          :disabled="checkoutStateStore.isLoading"
          class="btn btn--profile btn--checkout btn--smaller checkout-form__submit"
          type="submit"
        >
          <Icon
            v-if="checkoutStateStore.isLoading"
            type="spinner"
            size="tiny"
            class="icon--spinner"
          />
          <span v-if="checkoutStateStore.isLoading">{{ $t('loading') }}</span>
          <span v-if="!checkoutStateStore.isLoading">{{ $t('continue') }}</span>
        </button>
      </fieldset>
    </form>
  </div>
</template>
